import React, { createContext, useContext, useEffect, useState } from "react";
import { AuthAPI, clearSessionToken } from "@/lib/api";

const ThemeContext = createContext(null);
const AuthContext = createContext(null);

// Dark-only. The theme system is retained as a shim so any consumer calling
// `useTheme()` or `toggleTheme()` continues to work — they just always get "dark".
const THEMES = ["dark"];
const DEFAULT_THEME = "dark";

// Legacy server values are all coerced to dark now.
const _normalizeTheme = () => DEFAULT_THEME;

export function AppProviders({ children }) {
  const [theme] = useState(DEFAULT_THEME);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", DEFAULT_THEME);
    localStorage.setItem("v_theme", DEFAULT_THEME);
  }, []);

  const setTheme = () => { /* no-op — dark only */ };

  const refreshUser = async () => {
    try {
      const { data } = await AuthAPI.me();
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setAuthLoading(false);
    }
  };

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setAuthLoading(false);
      return;
    }
    refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    await AuthAPI.logout().catch(() => {});
    clearSessionToken();
    setUser(null);
  };

  return (
    <ThemeContext.Provider value={{ theme: DEFAULT_THEME, setTheme, themes: THEMES, toggleTheme: () => {} }}>
      <AuthContext.Provider value={{ user, setUser, authLoading, refreshUser, logout }}>
        {children}
      </AuthContext.Provider>
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
export const useAuth = () => useContext(AuthContext);
